/**
 * AllowAccessScreen — s119 (his rulings 2026-10-24; canvas "Orella Share Code Approval" B3P8vpegjN7Lo6BdJkMZFq,
 * P2 / P3 / P4, approved incl. en + es). Someone opened the patient's share link: nothing about the patient reaches
 * them until the patient taps Allow here. Allow -> a 15-minute read-only view for that doctor (P3). Deny -> the code
 * stops working and the patient makes a new one (P4). The push that brings the patient here names no one; the name is
 * only inside the app. Route: /case/:caseId/allow-access (the notification's deep link).
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Check, Lock } from 'lucide-react'
import { useI18n } from '../../i18n'
import { casesAPI } from '../../services/api'
import { useCaseStore } from '../../stores'
import { clinicianForms, clinicianFromText } from '../../utils/clinicianName.js'
import { appLocale } from '../../utils/appLanguage'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'

const whenOf = (iso, language) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(appLocale(language), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function AllowAccessScreen() {
  const { caseId } = useParams()
  const navigate = useNavigate()
  const { t, language } = useI18n()
  const loadCases = useCaseStore((s) => s.loadCases)
  const setCurrentCase = useCaseStore((s) => s.setCurrentCase)
  const [phase, setPhase] = useState('loading') // loading | request | allowed | denied | none | error
  const [req, setReq] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    casesAPI.getOpenRequest(caseId)
      .then((d) => {
        if (!alive) return
        if (d?.request) { setReq(d.request); setPhase('request'); return }
        if (d?.last && (d.last.state === 'allowed' || d.last.state === 'locked')) { setReq(d.last); setPhase('allowed'); return }
        setPhase('none')
      })
      .catch(() => { if (alive) setPhase('error') })
    return () => { alive = false }
  }, [caseId])

  const forms = clinicianForms(clinicianFromText(req?.clinicianTitle || ''))
  const who = req?.clinicianTitle ? forms : clinicianForms({ name: t('caseHome.aDoctor') })

  async function decide(decision) {
    if (!req || busy) return
    setBusy(true); setErr('')
    try {
      await casesAPI.decideOpenRequest(caseId, req.id, decision)
      setPhase(decision === 'allow' ? 'allowed' : 'denied')
      setReq((r) => ({ ...r, decidedAt: new Date().toISOString() }))
    } catch (e) {
      if (e?.code === 'request_not_pending' || e?.status === 409) setPhase('none')
      else setErr(t('shareApproval.errorRetry'))
    } finally { setBusy(false) }
  }

  async function makeNewCode() {
    // The denied code is gone; the share hub builds a new one for this case.
    try { await loadCases() } catch { /* the hub reads the case again */ }
    const found = (useCaseStore.getState().cases || []).find((c) => c.id === caseId)
    if (found) { setCurrentCase(found); navigate('/case/share-hub', { replace: true }) }
    else navigate(`/case/${caseId}`, { replace: true })
  }

  const Header = ({ title }) => (
    <header className="flex items-center gap-2.5 h-[60px] px-4 border-b border-[#E7EEF4]" style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}>
      <h1 className="m-0 text-[17px] font-extrabold" style={{ color: NAVY }}>{title}</h1>
    </header>
  )

  if (phase === 'loading') {
    return <div className="screen-min-h grid place-items-center bg-white"><OrellaSpinner size={24} /></div>
  }

  if (phase === 'denied') {
    return (
      <div className="screen-min-h bg-white flex flex-col" data-allow-access="denied">
        <Header title={t('shareApproval.offTitle')} />
        <div className="flex-1 px-6 pt-10 flex flex-col items-center text-center gap-4">
          <span className="w-[72px] h-[72px] rounded-full grid place-items-center bg-[#FEF3F2] text-[#B42318]"><Lock className="w-8 h-8" aria-hidden="true" /></span>
          <h2 className="m-0 text-2xl font-extrabold" style={{ color: NAVY }}>{t('shareApproval.offTitle')}</h2>
          <p className="m-0 text-base leading-relaxed text-slate-700">{t('shareApproval.offBody')}</p>
          <div className="w-full rounded-2xl bg-[#F4F8FB] px-4 py-3.5 text-left text-sm leading-relaxed text-slate-700">
            {t('shareApproval.offDetail', { ...who, when: whenOf(req?.decidedAt, language) })}
          </div>
        </div>
        <div className="px-5 pb-9 pt-4 flex flex-col gap-2.5">
          <button type="button" onClick={makeNewCode} className="h-[54px] rounded-full text-white font-bold text-[17px]" style={{ background: NAVY }}>{t('shareApproval.newCode')}</button>
          <button type="button" onClick={() => navigate(`/case/${caseId}`, { replace: true })} className="h-[54px] rounded-full border border-slate-300 bg-white font-bold text-[17px] text-slate-700">{t('shareApproval.notNow')}</button>
        </div>
      </div>
    )
  }

  if (phase === 'allowed') {
    return (
      <div className="screen-min-h bg-[#F4F8FB] flex flex-col" data-allow-access="allowed">
        <Header title={t('shareApproval.allowTitle')} />
        <div className="flex-1 p-4 flex flex-col gap-3.5">
          <div role="status" className="flex gap-3 items-center rounded-2xl border border-[#ABEFC6] bg-[#ECFDF3] p-3.5">
            <span className="w-9 h-9 rounded-full grid place-items-center bg-[#067647] text-white flex-none"><Check className="w-[18px] h-[18px]" strokeWidth={2.6} aria-hidden="true" /></span>
            <span className="text-[15px] font-semibold leading-snug text-[#054F31]">{t('shareApproval.canSee', who)}</span>
          </div>
          <div className="rounded-[20px] border border-[#E7EEF4] bg-white px-4 py-3.5">
            <div className="text-[15px] font-bold" style={{ color: NAVY }}>{t('shareApproval.noticeOpened', who)}</div>
            <div className="text-[13px] text-[#5F7A8E] mt-0.5">{t('shareApproval.noticeReadOnly')}</div>
          </div>
        </div>
        <div className="px-5 pb-9 pt-4">
          <button type="button" onClick={() => navigate(`/case/${caseId}`, { replace: true })} className="w-full h-[54px] rounded-full text-white font-bold text-[17px]" style={{ background: NAVY }}>{t('common.done', 'Done')}</button>
        </div>
      </div>
    )
  }

  if (phase !== 'request') {
    // Nothing waiting (answered elsewhere, lapsed, or no code): back to the case.
    return (
      <div className="screen-min-h bg-white flex flex-col" data-allow-access="none">
        <Header title={t('shareApproval.allowTitle')} />
        <div className="flex-1 px-6 pt-10 text-center text-base text-slate-700">{phase === 'error' ? t('shareApproval.errorRetry') : t('shareApproval.nothingWaiting')}</div>
        <div className="px-5 pb-9 pt-4"><button type="button" onClick={() => navigate(`/case/${caseId}`, { replace: true })} className="w-full h-[54px] rounded-full text-white font-bold text-[17px]" style={{ background: NAVY }}>{t('common.done', 'Done')}</button></div>
      </div>
    )
  }

  const place = [req.practice, [req.city, req.stateCode].filter(Boolean).join(', ')].filter(Boolean).join(', ')
  return (
    <div className="screen-min-h bg-white flex flex-col" data-allow-access="request">
      <Header title={t('shareApproval.allowTitle')} />
      <div className="flex-1 px-5 pt-5 flex flex-col gap-4">
        <div className="rounded-[20px] border-[1.5px] border-[#D6ECFB] bg-[#EFF8FF] p-[18px] flex flex-col gap-3.5">
          <div>
            <div className="text-lg font-extrabold leading-tight" style={{ color: NAVY }}>{t('shareApproval.wantsToOpen', who)}</div>
          </div>
          <dl className="m-0 grid grid-cols-[96px_minmax(0,1fr)] gap-x-2.5 gap-y-2 text-sm">
            {req.specialty ? <><dt className="text-[#456178]">{t('shareApproval.labelSpecialty')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{req.specialty}</dd></> : null}
            {place ? <><dt className="text-[#456178]">{t('shareApproval.labelOffice')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{place}</dd></> : null}
            {req.npi ? <><dt className="text-[#456178]">{t('shareApproval.labelNpi')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{req.npi}</dd></> : null}
            {req.requestedAt ? <><dt className="text-[#456178]">{t('shareApproval.labelAsked')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{whenOf(req.requestedAt, language)}</dd></> : null}
          </dl>
          {req.role === 'staff' && req.staffName ? (
            <div className="border-t border-[#D6ECFB] pt-2.5 text-[13px] text-[#456178]">
              {t('shareApproval.staffLine', { ...who, name: req.staffName, role: req.staffTitle || '' })}
            </div>
          ) : null}
        </div>
        <p className="m-0 text-[15px] leading-relaxed text-slate-700">{t('shareApproval.guidance')}</p>
        <p className="m-0 text-[15px] leading-relaxed text-slate-700">{t('shareApproval.limits')}</p>
        {err ? <p role="alert" className="m-0 text-sm text-red-700">{err}</p> : null}
      </div>
      <div className="px-5 pb-9 pt-4 flex flex-col gap-2.5">
        <button type="button" disabled={busy} onClick={() => decide('allow')} className="h-[54px] rounded-full text-white font-bold text-[17px] disabled:opacity-60" style={{ background: NAVY }}>{t('shareApproval.allow')}</button>
        <button type="button" disabled={busy} onClick={() => decide('deny')} className="h-[54px] rounded-full border-[1.5px] border-[#B42318] text-[#B42318] font-bold text-[17px] bg-white disabled:opacity-60">{t('shareApproval.deny')}</button>
        <div className="text-center text-[13px] text-[#5F7A8E]">{t('shareApproval.expires24')}</div>
      </div>
    </div>
  )
}
